import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import ModuleGraphCard from '../../components/subject-components/ModuleGraphCard';
import LoadingComponent from '../../components/LoadingComponent';
import { useNavigate } from 'react-router-dom';
import { API_BASE_URL } from "../../config";

const ModuleOverview = ({
  module_number,
  module_name,
  module_description,
  subject_name,
  exam_type,
}) => {
  const [subject, setSubject] = useState({});
  const [subjectModule, setSubjectModule] = useState({});
  const [questions, setQuestions] = useState([]);

  const [moduleStats, setModuleStats] = useState({
    totalQuestions: 0,
    mastered: 0,
    inProgress: 0,
    remaining: 0,
    masteryPercentage: 0,
    lastPracticedAt: null,
  });

  const [statsLoading, setStatsLoading] = useState(true);
  const [showMasteryInfo, setShowMasteryInfo] = useState(false);

  const studentInfo = localStorage.getItem('user');
  const studentId = JSON.parse(studentInfo || '{}')?.id;

  const navigate = useNavigate();

  const slugify = (s = "") =>
    String(s)
      .trim()
      .toLowerCase()
      .replace(/&/g, "and")
      .replace(/[^\w\s-]/g, "")
      .replace(/\s+/g, "-");

  const examSlug = slugify(exam_type);       // "BGCSE" -> "bgcse"
  const subjectSlug = slugify(subject_name); // "Biology" -> "biology"
  const moduleSlug = slugify(module_name);   // "Environmental Biology" -> "environmental-biology"

  const SUBJECT_BASE_PATH = `/${examSlug}/${subjectSlug}`;
  const MODULE_BASE_PATH = `${SUBJECT_BASE_PATH}/${moduleSlug}`;

  const fmtDate = (iso) =>
    iso
      ? new Date(iso).toLocaleString(undefined, {
          dateStyle: 'medium',
          timeStyle: 'short',
        })
      : null;

  const buildLearnUrl = ({ random, mode } = {}) => {
    const base = `${MODULE_BASE_PATH}/learn`;
    const qs = new URLSearchParams();
    if (Number.isFinite(random) && random > 0) qs.set('random', String(random));
    if (mode) qs.set('mode', mode);
    const q = qs.toString();
    return q ? `${base}?${q}` : base;
  };

  // Load subject
  useEffect(() => {
    const fetchSubject = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/subjects`, {
          params: {
            subjectName: subject_name,
            examType: exam_type.toLowerCase(),
          },
        });

        setSubject(response.data[0]);
      } catch (error) {
        console.error('Error loading subject', error);
      }
    };

    fetchSubject();
  }, [subject_name, exam_type]);

  // Load subject module
  useEffect(() => {
    if (!subject.id) return;

    const fetchSubjectModule = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/subject_modules`, {
          params: {
            moduleNumber: module_number,
            subjectId: subject.id,
          },
        });

        setSubjectModule(response.data[0]);
      } catch (error) {
        console.error('Error loading subject module', error);
      }
    };

    fetchSubjectModule();
  }, [subject, module_number]);

  // Load questions (fallback for canPractice / total)
  useEffect(() => {
    if (!subjectModule.id) return;

    const fetchQuestions = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/questions`, {
          params: { module: subjectModule.id },
        });

        setQuestions(response.data || []);
      } catch (error) {
        console.error('Error loading module questions', error);
      }
    };

    fetchQuestions();
  }, [subjectModule]);

  // Load module stats
  useEffect(() => {
    if (!subjectModule.id) return;
    if (!studentId) return;

    const fetchModuleStats = async () => {
      try {
        const response = await axios.get(
          `${API_BASE_URL}/api/module_mastery_statistics`,
          {
            params: {
              student_id: studentId,
              subject_module_id: subjectModule.id,
            },
          }
        );

        const data = response.data || {};

        setModuleStats({
          totalQuestions: data.total_questions || 0,
          mastered: data.mastered_questions || 0,
          inProgress: data.in_progress_questions || 0,
          remaining: data.not_started_questions || 0,
          masteryPercentage: data.mastery_percentage || 0,
          lastPracticedAt: data.last_practiced_at || null,
        });
      } catch (error) {
        console.error('Error loading module statistics', error);
      } finally {
        setStatsLoading(false);
      }
    };

    setStatsLoading(true);
    fetchModuleStats();
  }, [subjectModule, studentId]);

  const mastery = moduleStats.masteryPercentage || 0;
  const progress = Math.round(Math.min(100, Math.max(0, mastery)));
  const progressBarColor = progress === 100 ? 'bg-emerald-500' : 'bg-green-600';

  const totalInModule = moduleStats.totalQuestions || questions.length || 0;
  const canPractice = totalInModule > 0;

  const notStartedPercentage =
    moduleStats.totalQuestions > 0
      ? Math.round((moduleStats.remaining / moduleStats.totalQuestions) * 100)
      : 100;

  const isFreshModule = notStartedPercentage === 100;
  const isFullyMastered = progress === 100;

  const primaryCtaLabel = isFreshModule ? 'Start Practice' : 'Continue Studying';

  const primaryCtaDescription = (() => {
    if (isFullyMastered) {
      return "You've fully mastered this module. Jump back in anytime to stay sharp!";
    }
    if (isFreshModule) {
      return "You haven’t tried any questions in this module yet. Start practice to build mastery.";
    }
    return "You're off to a strong start. Continue practice to unlock full mastery!";
  })();

  const statusMeta = useMemo(() => {
    if (progress === 0) return { label: 'Not Started', pill: 'bg-gray-100 text-gray-700 border-gray-200' };
    if (progress < 40) return { label: 'Getting Started', pill: 'bg-sky-50 text-sky-700 border-sky-200' };
    if (progress < 80) return { label: 'Building', pill: 'bg-amber-50 text-amber-700 border-amber-200' };
    if (progress < 100) return { label: 'Almost Ready', pill: 'bg-emerald-50 text-emerald-700 border-emerald-200' };
    return { label: 'Mastered', pill: 'bg-emerald-100 text-emerald-800 border-emerald-200' };
  }, [progress]);

  const lastPracticeLabel = fmtDate(moduleStats.lastPracticedAt) || '—';

  const QUICK_PRACTICE_LIMIT = 25;

  const handleViewLatestSummary = () => {
    if (!subjectModule?.id) return;

    navigate(`${MODULE_BASE_PATH}/learn-summary`, {
      state: {
        subjectModuleId: subjectModule.id,
        moduleNumber: subjectModule.module_number,
        subject_module_id: subjectModule.id,
        module_number: subjectModule.module_number,
      },
    });
  };

  const handleStartLearning = () => {
    window.location.href = buildLearnUrl();
  };

  const handleQuickPractice = (limit = QUICK_PRACTICE_LIMIT) => {
    window.location.href = buildLearnUrl({ random: limit, mode: 'quick' });
  };

  if (statsLoading) return <LoadingComponent />;

  return (
    <div>
      {/* HERO (match SubjectOverview vibe) */}
      <div className="relative mt-0 py-5 px-5 my-5 w-full bg-[#f0f0f0]">
        <div className="absolute inset-0 -z-10 bg-[radial-gradient(circle_at_top,rgba(2,41,53,0.08),transparent_55%)]" />

        <div className="rounded-2xl border border-white/60 bg-white/70 backdrop-blur-md shadow-md overflow-hidden">
          {/* Header */}
          <div className="px-6 pt-6 pb-4">
            <div className="flex flex-col gap-4">
              <div className="flex flex-col gap-3">
                {/* Row 1: exam/module line + back link (full width) */}
                <div className="flex items-center justify-between gap-3 w-full">
                  <div className="min-w-0 text-[10px] sm:text-xs uppercase tracking-[0.2em] text-gray-500 truncate">
                    {String(exam_type || "").toUpperCase()} • {subject_name} • Module {module_number}
                  </div>

                  <a
                    href={SUBJECT_BASE_PATH}
                    className="shrink-0 whitespace-nowrap text-[10px] sm:text-xs font-semibold tracking-normal text-sky-700 hover:text-sky-900 underline"
                  >
                    ← {subject_name} Home
                  </a>
                </div>

                {/* Row 2: module name + status/meta (your existing row) */}
                <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
                  <div className="min-w-0">
                    <h1 className="mt-1 text-3xl sm:text-5xl font-semibold text-gray-900 break-words">
                      {module_name}
                    </h1>
                  </div>

                  <div className="flex items-center gap-2 sm:gap-3">
                    <span className={`text-xs font-semibold px-3 py-1 rounded-full border ${statusMeta.pill}`}>
                      {statusMeta.label}
                    </span>

                  {/* Mastery info icon */}
                  <div className="relative shrink-0">
                    <button
                      type="button"
                      onClick={() => setShowMasteryInfo((prev) => !prev)}
                      className="w-7 h-7 flex items-center justify-center rounded-full border border-sky-500 text-[11px] font-bold text-sky-600 bg-white hover:bg-sky-50"
                      aria-label="What is module mastery?"
                    >
                      i
                    </button>

                    {showMasteryInfo && (
                      <div className="absolute right-0 mt-2 w-72 rounded-md bg-white text-[11px] text-gray-700 shadow-lg border border-gray-200 z-10">
                        <div className="p-2">
                          <p className="font-semibold mb-1 text-xs">How Module Mastery Works</p>
                          <p className="mb-1">
                            This percentage shows how many questions in this module you’ve{' '}
                            <span className="font-semibold">fully mastered</span>, based on your past attempts.
                          </p>
                          <p className="mb-1">
                            Written answers are mastered as soon as you get one fully right on your own.
                          </p>
                          <p className="mb-0">
                            Multiple choice is mastered the same way — unless you have had it wrong
                            before, in which case it takes two correct answers in a row, since you
                            have already been shown the right option.
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              </div>

              {/* Progress block */}
              <div className="w-full">
                <div className="mb-2 text-xs text-gray-500">Module Mastery</div>

                <div className="w-full rounded-full border border-white/60 bg-white/40 py-2">
                  <div className="relative h-4 sm:h-5 w-full rounded-full overflow-hidden bg-gray-200/70 border border-gray-300/40">
                    <div
                      style={{ width: `${progress}%` }}
                      className={`${progressBarColor} h-full rounded-full transition-all duration-300 relative overflow-hidden`}
                    >
                      {progress > 0 && (
                        <span
                          className={`absolute top-1/2 -translate-y-1/2 ${
                            progress < 35 ? 'left-3' : 'right-3'
                          } text-white text-[10px] sm:text-xs font-semibold leading-none drop-shadow whitespace-nowrap`}
                          aria-hidden="true"
                        >
                          {progress}%
                        </span>
                      )}
                    </div>

                    {progress === 0 && (
                      <span
                        className="absolute top-1/2 -translate-y-1/2 left-3 text-gray-700 text-[10px] sm:text-xs font-semibold leading-none"
                        aria-hidden="true"
                      >
                        0%
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="h-px bg-[#f0f0f0]" />

          {/* Description */}
          <div className="px-6 py-5 bg-white/40">
            <p className="font-thin text-sm sm:text-base text-gray-800 leading-relaxed">
              {module_description}
            </p>
          </div>
        </div>

        {/* Cards row */}
        <div className="w-full grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6 mt-6">
          {/* Practice Status */}
          <div className="rounded-2xl border border-white/60 bg-white/60 backdrop-blur-md shadow-md p-5">
            <h2 className="text-sm font-semibold text-gray-800 mb-4">Practice</h2>

            <button
              type="button"
              className="mb-2 w-full rounded-lg bg-emerald-600 px-4 py-3 text-sm font-semibold text-white hover:bg-emerald-700 transition shadow disabled:bg-gray-300 disabled:text-gray-600 disabled:cursor-not-allowed"
              onClick={handleStartLearning}
              disabled={!canPractice}
            >
              {primaryCtaLabel}
            </button>

            <p className="text-xs text-gray-600">{primaryCtaDescription}</p>

            <div className="mt-4">
              <button
                type="button"
                onClick={() => handleQuickPractice(QUICK_PRACTICE_LIMIT)}
                disabled={!canPractice}
                className={[
                  'w-full rounded-lg border px-4 py-3 text-sm font-semibold transition',
                  canPractice
                    ? 'border-emerald-600 text-emerald-700 bg-white hover:bg-emerald-50'
                    : 'border-gray-200 text-gray-400 bg-gray-50 cursor-not-allowed',
                ].join(' ')}
              >
                Quick Study
              </button>

              <p className="mt-2 text-[11px] text-gray-600">
                Set of {QUICK_PRACTICE_LIMIT} questions from this module.
              </p>
            </div>

            {/* Learning summary link */}
            <div className="mt-5 pt-4 border-t border-white/80">
              <div className="flex items-center justify-between gap-3">
                <div className="text-xs text-gray-600">
                  <span className="font-semibold text-gray-800">Last practice session:</span>{' '}
                  {lastPracticeLabel}
                </div>

                <button
                  type="button"
                  onClick={handleViewLatestSummary}
                  className="text-xs font-semibold text-sky-700 hover:text-sky-900 underline whitespace-nowrap"
                >
                  View Learning Summary
                </button>
              </div>
            </div>
          </div>

          {/* Mastery breakdown graph card */}
          <div className="rounded-2xl border border-white/60 bg-white/60 backdrop-blur-md shadow-md p-5">
            <ModuleGraphCard
              totalQuestions={moduleStats.totalQuestions}
              mastered={moduleStats.mastered}
              inProgress={moduleStats.inProgress}
              remaining={moduleStats.remaining}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ModuleOverview;
